// ─── 🔁 Cambia organizzatore, nella scheda della partita (fase 3, R5) ─────────
//
// Dal mockup approvato il 26/09/2026: la Segreteria sceglie chi diventa
// l'organizzatore — chi gioca già, o un altro socio (e allora se il primo
// gioca lo stesso o gli lascia il posto) — e il cambio si fa subito. Una
// richiesta partita dal bot che aspetta la risposta si vede qui, e si può
// lasciare com'era.
// 🔄 30/09/2026: via l'acconto, e con lui la scelta di come il nuovo
// organizzatore pagava il suo.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type CambioOrganizzatore, type PersonaTrovata, type SchedaPrenotazione } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Guasto } from '../../components/pezzi.js';

export function CambioOrganizzatoreDellaScheda({
  p,
  puoModificare,
}: {
  p: SchedaPrenotazione;
  puoModificare: boolean;
}): React.JSX.Element | null {
  const qc = useQueryClient();
  const [apre, setApre] = useState(false);
  const [chi, setChi] = useState<string | null>(null);
  const [altro, setAltro] = useState<{ id: string; nome: string } | null>(null);
  const [testo, setTesto] = useState('');
  const [trovati, setTrovati] = useState<PersonaTrovata[]>([]);
  const [lascia, setLascia] = useState(false);

  const applicabile =
    p.type === 'partita' &&
    p.status === 'confirmed' &&
    p.serie === null &&
    p.organizerMemberId !== null &&
    Date.parse(p.startsAt) > Date.now();

  const inCorso = useQuery({
    queryKey: ['cambio-organizzatore', p.id],
    queryFn: () => chiama<CambioOrganizzatore | null>('GET', `/bookings/${p.id}/organizer-change`),
    enabled: applicabile,
  });
  const rileggi = () => {
    void qc.invalidateQueries({ queryKey: ['cambio-organizzatore', p.id] });
    void qc.invalidateQueries({ queryKey: ['prenotazione', p.id] });
    void qc.invalidateQueries({ queryKey: ['calendario'] });
    void qc.invalidateQueries({ queryKey: ['organizzatore-partita', p.id] });
  };

  useEffect(() => {
    if (testo.trim().length < 2) {
      setTrovati([]);
      return;
    }
    let annullato = false;
    const t = setTimeout(() => {
      void chiama<{ items: PersonaTrovata[] }>('GET', `/members/lookup?q=${encodeURIComponent(testo.trim())}`)
        .then((r) => !annullato && setTrovati(r.items))
        .catch(() => !annullato && setTrovati([]));
    }, 250);
    return () => {
      annullato = true;
      clearTimeout(t);
    };
  }, [testo]);

  const altri = p.players.filter((g) => g.memberId !== null && g.memberId !== p.organizerMemberId);
  const scelto = chi === 'altro' ? (altro?.id ?? null) : chi;
  const eAltro = chi === 'altro';
  const piena = p.players.length >= 4;

  const cambia = useMutation({
    meta: { fatto: (d) => `adesso l’organizzatore è ${(d as CambioOrganizzatore).toName}` },
    mutationFn: () =>
      chiama<CambioOrganizzatore>('POST', `/bookings/${p.id}/organizer-change`, {
        toMemberId: scelto,
        leavesPlace: eAltro && lascia,
      }),
    onSuccess: () => {
      setApre(false);
      setChi(null);
      setAltro(null);
      rileggi();
    },
  });
  const ritira = useMutation({
    meta: { fatto: 'cambio d’organizzatore ritirato: resta com’era' },
    mutationFn: () => chiama('POST', `/bookings/${p.id}/organizer-change/withdraw`),
    onSuccess: rileggi,
  });

  if (!applicabile || !puoModificare) return null;
  const c = inCorso.data;

  if (c) {
    return (
      <div className="finestra-caparra">
        <p>
          🔁 L’organizzatore <strong>diventa {c.toName}</strong>
          {c.leavesPlace ? ', che prende il posto di ' + c.fromName : ''} ·{' '}
          <span className="pill-caparra pill-attesa">attende la sua risposta</span>
        </p>
        <p className="tenue piccolo">
          Fino ad allora resta {c.fromName}. Se la risposta non arriva entro le{' '}
          {new Date(c.expiresAt).toLocaleTimeString('it-IT').slice(0, 5)}, resta tutto com’era.
        </p>
        <div className="azioni-caparra">
          <button
            type="button"
            className="bordato"
            disabled={ritira.isPending}
            onClick={() => ritira.mutate()}
          >
            Lascia com’era
          </button>
        </div>
        <Guasto errore={ritira.error} />
      </div>
    );
  }

  if (!apre) {
    return (
      <div className="azioni-caparra">
        <button type="button" className="bordato" onClick={() => setApre(true)}>
          🔁 Cambia organizzatore
        </button>
      </div>
    );
  }

  return (
    <div className="finestra-caparra">
      <strong>🔁 Chi diventa l’organizzatore?</strong>
      <p className="piccolo">
        L’organizzatore riceve gli avvisi sulla partita, può metterla in Partite aperte o disdirla.
      </p>
      <div className="scelte-caparra">
        {altri.map((g) => (
          <label key={g.id}>
            <input
              type="radio"
              name="cambio-chi"
              checked={chi === g.memberId}
              onChange={() => setChi(g.memberId)}
            />{' '}
            {g.displayName} <span className="tenue piccolo">· gioca già</span>
          </label>
        ))}
        <label>
          <input type="radio" name="cambio-chi" checked={eAltro} onChange={() => setChi('altro')} /> Un altro
          socio
        </label>
      </div>
      {eAltro && (
        <>
          {altro ? (
            <p>
              <strong>{altro.nome}</strong>{' '}
              <button type="button" className="piatto" onClick={() => setAltro(null)}>
                cambia
              </button>
            </p>
          ) : (
            <label className="campo">
              <span>Cerca il socio</span>
              <input value={testo} onChange={(e) => setTesto(e.target.value)} placeholder="cognome o nome" />
            </label>
          )}
          {!altro && trovati.length > 0 && (
            <ul className="trovati">
              {trovati
                .filter((s) => s.id !== p.organizerMemberId && !p.players.some((g) => g.memberId === s.id))
                .map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      className="piatto"
                      onClick={() => {
                        setAltro({ id: s.id, nome: s.displayName });
                        setTesto('');
                        setTrovati([]);
                      }}
                    >
                      {s.displayName}
                    </button>
                  </li>
                ))}
            </ul>
          )}
          <div className="scelte-caparra">
            <label>
              <input
                type="radio"
                name="cambio-posto"
                checked={!lascia}
                disabled={piena}
                onChange={() => setLascia(false)}
              />{' '}
              chi organizzava gioca lo stesso{piena ? ' (la partita è piena)' : ''}
            </label>
            <label>
              <input
                type="radio"
                name="cambio-posto"
                checked={lascia || piena}
                onChange={() => setLascia(true)}
              />{' '}
              gli lascia il suo posto in campo
            </label>
          </div>
        </>
      )}
      <Guasto errore={cambia.error} />
      <div className="azioni-caparra">
        <button
          type="button"
          disabled={cambia.isPending || scelto === null}
          onClick={() => {
            if (eAltro && piena) setLascia(true);
            cambia.mutate();
          }}
        >
          Cambia organizzatore
        </button>
        <button type="button" className="bordato" onClick={() => setApre(false)}>
          Lascia stare
        </button>
      </div>
    </div>
  );
}
